import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DigitalDepositAddress } from '../../../../../../packages/utx-access-client/src/react';

export interface ChainsData extends Array<{ chain: string; chainName: string | undefined }> {}

export interface ChainData {
  chain: string;
  chainName: string | undefined;
}

interface UseChainSelectorParams {
  value?: string;
  onValueChange: (value: string) => void;
  onChainChange?: (value: ChainData | undefined) => void;
  chainsDisplay: { chain: string; chainName: string | undefined }[];
}

export const useChainSelector = ({ value, onValueChange, onChainChange, chainsDisplay }: UseChainSelectorParams) => {
  const { t } = useTranslation('earn');

  const chainsData = useMemo(() => {
    if (!chainsDisplay) return [];
    let data: ChainsData = [];
    data = chainsDisplay.sort((a, b) => Number(a?.chainName?.charCodeAt(0)) - Number(b?.chainName?.charCodeAt(0)));
    return data;
  }, [chainsDisplay]);

  const [hasFocus, setHasFocus] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedChainId, setSelectedChainId] = useState(value || '');

  const selectedChain: ChainData | undefined = useMemo(() => {
    return chainsData.find((chain) => chain?.chain === selectedChainId);
  }, [value, selectedChainId]);

  const displayChainData = useMemo(() => {
    if (!searchText) return chainsData;
    return chainsData.filter((chain) => {
      return (
        chain?.chain?.toLowerCase().includes(searchText.toLowerCase()) || chain?.chainName?.toLowerCase().includes(searchText.toLowerCase())
      );
    });
  }, [searchText, chainsData]);

  useEffect(() => {
    if (value) setSelectedChainId(value);
  }, [value]);

  useEffect(() => {
    if (!!selectedChain && !!onChainChange) {
      onChainChange(selectedChain);
    }
  }, [selectedChain]);

  useEffect(() => {
    if (!hasFocus) return;
    const close = () => {
      setHasFocus(false);
    };
    document.addEventListener('click', close);
    return () => {
      document.removeEventListener('click', close);
    };
  }, [hasFocus]);

  const changeFocus = (value: boolean) => setHasFocus(value);
  const changeSearchText = (value: string) => setSearchText(value);
  const selectChainId = (id: string) => {
    setSelectedChainId(id);
    onValueChange(id);
  };

  return {
    hasFocus,
    selectedChain,
    selectedChainId,
    displayChainData,
    changeFocus,
    changeSearchText,
    selectChainId
  };
};
